// CSS
import './LandingNew.css';
// React hooks
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Skybase hooks
import { useData } from '../../context/DataContext';
// Skybase components
import NavBarNew from '../../components/NavBarNew/NavBarNew';
import FooterNew from '../../components/FooterNew/FooterNew';
import DatabaseInstance from '../DatabaseNew/DatabaseInstance/DatabaseInstance';
// External hooks/components
import { useClerk, useUser, Show } from '@clerk/react';                                     // user authe.
import { useMediaQuery } from 'react-responsive';                                           // responsive CSS
// Media assets
import AnimatedHorizontalLogo from '../../assets/videos/skybase-framer-logo.webm';
import LandingShaderVideo from '../../assets/videos/landing-shader-video.webm';

// ==========
// LandingNew – represents the '/' Landing page.
// ==========
function LandingNew() {

    // destructure global context
    const { availableEnginesJson, categoryJson } = useData();

    // init hooks
    const navigate = useNavigate('../');
    const clerk = useClerk();
    const clerkUser = useUser();

    // state vars
    const [activeProductInfo, setActiveProductInfo] = useState(1);

    // responsive CSS
    const isDesktop = useMediaQuery({ minWidth: 1199 });
    const isTablet = useMediaQuery({
        minWidth: 651,
        maxWidth: 1199
    });
    const isPhone = useMediaQuery({ maxWidth: 650});

    // // cycle durations
    // const durations = {
    //     1: 10000, // X ms
    //     2: 18000, // Y ms
    //     3: 33000, // Z ms
    // };

    // // =====================================
    // // 'activeProductInfo' Looping Animation – the infinite-looping cycle to animate the Product Information carousel (vibe-coded)
    // // =====================================
    // useEffect(() => {
    //     const timeout = setTimeout(() => {
    //         setActiveProductInfo((prev) => (prev === 3 ? 1 : prev + 1));
    //     }, durations[activeProductInfo]);

    //     return () => clearTimeout(timeout);
    // }, [activeProductInfo]);

    
    // ==================================
    // onClick – Product Information Card
    // ==================================
    const handleProductInformationCardClick = (num) => {
        
        // immediately jump to specified info. card
        setActiveProductInfo(num);
    }


    // =====================================
    // Available Databases Looping Animation – the infinite-looping cycle to animate the Available Databases carousel (vibe-coded)
    // =====================================
    // index tracking
    const [activeFilterIndex, setActiveFilterIndex] = useState(0);                                      // current index
    const activeCategoryFilterJson = categoryJson[activeFilterIndex];                                   // the active Category JSON
    const [isAvailableDatabaseCycleStopped, setIsAvailableDatabaseCycleStopped] = useState(true);      // stopping condition (user manually disrupts cycle) // NOTE: Currently TRUE until more DBs from different categories are added!!!
    useEffect(() => {

        // vibe-coded
        if (!isAvailableDatabaseCycleStopped) {
            const timeout = setTimeout(() => {
                setActiveFilterIndex((prev) => (prev + 1) % categoryJson.length);
            }, 3000);

            return () => clearTimeout(timeout);
        }
    }, [activeFilterIndex, isAvailableDatabaseCycleStopped]);


    // ========================================
    // onClick – Available Database Filter Item
    // ========================================
    const handleAvailableDatabaseFilterItemClick = (num) => {
        
        // immediately stop the looping cycle (permanently, until page refresh)
        setIsAvailableDatabaseCycleStopped(true);

        // jump to the selected filter
        setActiveFilterIndex(num);
    }


    // ====================
    // onClick – CTA Button
    // ====================
    const handleCTAButtonClick = () => {
        
        // Condition: Re-direct to '/create-account' if NO <Clerk JWT>
        if (!clerkUser.isSignedIn) { 
            navigate('../create-account');
        
        // Contition #2: Otherwise, re-direct to '/library'
        } else {
            navigate('../library');
        };
    };



    // JSX Component
    return(
        <div className='LandingPage'>
            
            {/* Aesthetic background */}
            <div className='LandingPage-aesthetic-bg'></div>

            {/* Navigation Bar */}
            <NavBarNew/>

            {/* Main content */}
            <div className='LandingPage-main-content'>

                {/* === Demo (desktop & tablet) === */}
                { (isDesktop || isTablet) &&

                    <>
                        {/* @Framer Animated Video (Visual) */}
                        <video
                            className='LandingPage-main-content-visual'
                            autoPlay
                            muted
                            playsInline>
                            <source src={LandingShaderVideo} type="video/webm"/>
                        </video>

                        {/* Product demo */}
                        <div className='LandingPage-main-content-product-demo SearchPage-fade-in'>
                            <video
                                autoPlay
                                muted
                                playsInline
                                preload="metadata"
                                loop
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                    borderRadius: "12px",
                                }}
                                >
                                <source
                                    src="/videos/skybase-hero-video-1080.mp4"
                                    type="video/mp4"
                                />
                            </video>
                        </div>
                    </>
                }

                {/* === Demo (phone) === */}
                { isPhone &&
                    <>
                        {/* @Framer Animated Video (Visual) */}
                        <video
                            className='LandingPage-main-content-visual-mobile'
                            autoPlay
                            muted
                            playsInline>
                            <source src={LandingShaderVideo} type="video/webm"/>
                        </video>

                        {/* Product demo */}
                        <div className='LandingPage-main-content-product-demo-mobile SearchPage-fade-in'>
                            <video
                                autoPlay
                                muted
                                playsInline
                                preload="metadata"
                                loop
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                    borderRadius: "12px",
                                }}
                                >
                                <source
                                    src="/videos/skybase-mobile-hero-video-1080.mp4"
                                    type="video/mp4"
                                />
                            </video>
                        </div>
                    </>
                }

                {/* Text content */}
                <div className='LandingPage-main-content-text-content'>

                    {/* Dynamic tagline */}
                    <div className='LandingPage-main-content-text-content-dynamic-tagline'>
                        { isDesktop ? "Instantly search through over 100,000+ FAA laws and regulations." : "The #1 Search Engine for U.S. Pilots" }
                    </div>

                    {/* Product statement */}
                    <div className='LandingPage-main-content-text-content-audience-statement'>
                        { isDesktop ? "The #1 Fastest and #1 Most Accurate Search Engine for U.S. Pilots." : "Search through over 100,000+ FAA laws, guidance documents, and aircraft maintenance publications in seconds." }
                    </div>

                    {/* Dynamic CTA button */}
                    <div
                        className='LandingPage-main-content-text-content-cta-button'
                        onClick={() => handleCTAButtonClick()}>
                        <Show when="signed-out">Create New Account ↗</Show>
                        <Show when="signed-in">Select a database ↗</Show>
                    </div>

                </div>
            </div>

            {/* SECTION: 'Product Information' */}
            {/* Section title */}
            { !isPhone && <div className='LandingPage-product-information-section-title'>How The #1 Aerospace Search Engine Works</div>}
            {isPhone && <div className='LandingPage-product-information-section-title'>How Skybase Works</div>}
            
            {/* Section (literal) */}
            <div className='LandingPage-product-information-section'>

                { isTablet &&
                    <div className='LandingPage-product-information-section-content-right'>
                            <div className='LandingPage-product-information-section-content-right-media'>

                                {/* Video 1 */}
                                { activeProductInfo === 1 && 
                                    <video
                                        autoPlay
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onEnded={() => setActiveProductInfo((prev) => (prev === 3 ? 1 : prev + 1))}
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                            borderRadius: "12px",
                                        }}
                                        >
                                        <source
                                            src="/videos/skybase-database-selection-1080.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                }


                                {/* Video 2 */}
                                { activeProductInfo === 2 && 
                                    <video
                                        autoPlay
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onEnded={() => setActiveProductInfo((prev) => (prev === 3 ? 1 : prev + 1))}
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                            borderRadius: "12px",
                                        }}
                                        >
                                        <source
                                            src="/videos/skybase-search-1080.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                }

                                {/* Video 3 */}
                                { activeProductInfo === 3 && 
                                    <video
                                        autoPlay
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onEnded={() => setActiveProductInfo((prev) => (prev === 3 ? 1 : prev + 1))}
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                            borderRadius: "12px",
                                        }}
                                        >
                                        <source
                                            src="/videos/skybase-history-and-bookmarks-1080.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                }
                            </div>
                    </div>
                }

                {/* Section content */}
                <div className='LandingPage-product-information-section-content'>
                    
                    {/* Left content */}
                    <div className='LandingPage-product-information-section-content-left'>
                        
                        {/* ..Select */}
                        <div onClick={() => handleProductInformationCardClick(1)} className={`LandingPage-product-information-direction-card ${activeProductInfo === 1 ? 'active' : ''}`}>
                            <div className={`LandingPage-product-information-direction-card-title ${activeProductInfo === 1 ? 'active' : ''}`}>Select a FAA database.</div>
                            <div className={`LandingPage-product-information-direction-card-details ${activeProductInfo === 1 ? 'active' : ''}`}>Choose from our growing library of databases containing 15,500+ FAA documents across seven different aviation categories.</div>
                            <div className={`LandingPage-product-information-direction-card-cta-button ${activeProductInfo === 1 ? 'active' : ''}`}><div onClick={() => handleCTAButtonClick()} className={`LandingPage-product-information-direction-card-cta-button-text ${activeProductInfo === 1 ? 'active' : ''}`}><Show when="signed-out">Get started now ↗</Show><Show when="signed-in">Select a database ↗</Show></div></div>
                        </div>

                        {/* ..Search */}
                        <div onClick={() => handleProductInformationCardClick(2)} className={`LandingPage-product-information-direction-card ${activeProductInfo === 2 ? 'active' : ''}`}>
                            <div className={`LandingPage-product-information-direction-card-title ${activeProductInfo === 2 ? 'active' : ''}`}>Create a search query for what you need.</div>
                            <div className={`LandingPage-product-information-direction-card-details ${activeProductInfo === 2 ? 'active' : ''}`}>Our intelligence system scans the entire database and finds the most relevent documentation within seconds.</div>
                            <div className={`LandingPage-product-information-direction-card-cta-button ${activeProductInfo === 2 ? 'active' : ''}`}><div onClick={() => handleCTAButtonClick()} className={`LandingPage-product-information-direction-card-cta-button-text ${activeProductInfo === 2 ? 'active' : ''}`}><Show when="signed-out">Get started now ↗</Show><Show when="signed-in">Select a database ↗</Show></div></div>
                        </div>

                        {/* ..Save */}
                        <div onClick={() => handleProductInformationCardClick(3)} className={`LandingPage-product-information-direction-card ${activeProductInfo === 3 ? 'active' : ''}`}>
                            <div className={`LandingPage-product-information-direction-card-title ${activeProductInfo === 3 ? 'active' : ''}`}>Instantly bookmark and revisit documents.</div>
                            <div className={`LandingPage-product-information-direction-card-details ${activeProductInfo === 3 ? 'active' : ''}`}>Bookmark documents and quickly revisit previous search results in one click.</div>
                            <div className={`LandingPage-product-information-direction-card-cta-button ${activeProductInfo === 1 ? 'active' : ''}`}><div onClick={() => handleCTAButtonClick()} className={`LandingPage-product-information-direction-card-cta-button-text ${activeProductInfo === 3 ? 'active' : ''}`}><Show when="signed-out">Get started now ↗</Show><Show when="signed-in">Select a database ↗</Show></div></div>
                        </div>
                    </div>

                    {/* Right content (tablet & desktop only) */}
                    { isDesktop && 
                        <div className='LandingPage-product-information-section-content-right'>
                            <div className='LandingPage-product-information-section-content-right-media'>

                                {/* Video 1 */}
                                { activeProductInfo === 1 && 
                                    <video
                                        autoPlay
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onEnded={() => setActiveProductInfo((prev) => (prev === 3 ? 1 : prev + 1))}
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                            borderRadius: "12px",
                                        }}
                                        >
                                        <source
                                            src="/videos/skybase-database-selection-1080.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                }


                                {/* Video 2 */}
                                { activeProductInfo === 2 && 
                                    <video
                                        autoPlay
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onEnded={() => setActiveProductInfo((prev) => (prev === 3 ? 1 : prev + 1))}
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                            borderRadius: "12px",
                                        }}
                                        >
                                        <source
                                            src="/videos/skybase-search-1080.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                }

                                {/* Video 3 */}
                                { activeProductInfo === 3 && 
                                    <video
                                        autoPlay
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onEnded={() => setActiveProductInfo((prev) => (prev === 3 ? 1 : prev + 1))}
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                            borderRadius: "12px",
                                        }}
                                        >
                                        <source
                                            src="/videos/skybase-history-and-bookmarks-1080.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>

            
            {/* SECTION: 'Available Databases' */}
            {/* Section title */}
            { !isPhone && <div className='LandingPage-available-database-section-title'>Our Mission: All FAA Documents On One Platform.</div>}
            { isPhone && <div className='LandingPage-available-database-section-title'>Available Databases</div>}
            
            
            
            {/* Section (literal) */}
            <div className='LandingPage-available-databases-section'>

                {/* Section content */}
                <div className='LandingPage-available-databases-section-content'>
                    
                    
                    {/* Left content */}
                    { isDesktop && 
                    <div className='LandingPage-available-databases-section-content-left'>
                        {categoryJson?.map( (cat_json, index) => (
                            <div key={index} onClick={() => handleAvailableDatabaseFilterItemClick(index)} className={`LandingPage-available-databases-section-content-left-item ${activeCategoryFilterJson === cat_json ? 'active' : ''}`}>
                                <div className='LandingPage-available-databases-section-content-left-item-content'>
                                    <div className='LandingPage-available-databases-section-content-left-item-title'>{cat_json.public_display_name}</div>
                                    <div className='LandingPage-available-databases-section-content-left-item-details'>{ cat_json.public_display_name.toLowerCase().includes("aviation") ? "15,000+ Documents" : "Coming Soon"}</div>
                                    <div onClick={() => navigate('../library', { state: { defaultCategoryFilterId: cat_json.id } })} className={`LandingPage-available-databases-section-content-left-item-cta-button ${activeCategoryFilterJson === cat_json ? 'active' : ''}`}>View all databases ↗</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    }

                    {/* Right content */}
                    <div className='LandingPage-available-databases-section-content-right'>
                        <div className='LandingPage-available-databases-section-content-right-repeater'>

                            {/* == (desktop) == */}
                            { isDesktop && 
                                <>
                                    {availableEnginesJson?.filter((engine_json => engine_json.category_id === activeCategoryFilterJson?.id)).map( (engine_json, index) => (
                                        <div key={index} style={{ "--stagger-index": index }} className='LandingPage-available-databases-section-content-right-repeater-instance'>
                                            <DatabaseInstance databaseJson={engine_json} key={index}/>
                                        </div>
                                    ))}
                            

                                    {availableEnginesJson?.filter((engine_json => engine_json.category_id === activeCategoryFilterJson?.id)).length === 0 && 
                                    <div className='LandingPage-available-databases-section-content-right-repeater--empty-content'><span className='SearchPage-fade-in'>More databases coming soon.</span></div>
                                    }

                                </>
                            }

                            {/* == (tablet & phone) == */}
                            { (isTablet || isPhone) &&
                                <>
                                    {availableEnginesJson?.map( (engine_json, index) => (
                                        <div key={index} style={{ "--stagger-index": index }} className='LandingPage-available-databases-section-content-right-repeater-instance'>
                                            <DatabaseInstance databaseJson={engine_json} key={index}/>
                                        </div>
                                    ))}
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {/* Section CTA Button */}
            <div className='LandingPage-view-all-available-databases-cta'>
                <div onClick={() => navigate('../library')} className='LandingPage-view-all-available-databases-cta-button'>View all<b>{availableEnginesJson?.length}</b>of our databases ↗</div>
            </div>

            {/* SECTION: 'Available Platforms' */}
            { isDesktop && 
                <>
                    {/* Section title */}
                    <div className='LandingPage-available-platforms-section-title'>Available Now. Coming Soon to iPad.</div>
                    
                    {/* Section (literal) */}
                    
                    <div className='LandingPage-available-platforms-section'>
                        
                        {/* Section content */}
                        <div className='LandingPage-available-platforms-section-content'>
                            
                            {/* 'Website' */}
                            <div className='LandingPage-available-platforms-section-content-col'>
                                {/* ..platform name */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-name'>Online Website</div>
                                {/* ..platform status */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-status'>Available Now</div>
                                {/* ..platform demo */}
                                <div className='LandingPage-available-platforms-section-content-col-website-platform-demo'>
                                    <video
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            display: "block",
                                            borderRadius: "12px",
                                        }}
                                        >
                                        <source
                                            src="/videos/skybase-search-1080.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                </div>
                                {/* ..availability */}
                                <div className='LandingPage-available-platforms-section-content-col-availability-section'>Chrome, Safari, Firefox, Edge, etc.</div>
                                {/* ..platform cta-button */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-cta-button' onClick={() => handleCTAButtonClick()}><Show when="signed-out">Create New Account ↗</Show><Show when="signed-in">Select a database ↗</Show></div>
                            </div>

                            {/* --- dividing line --- */}
                            <div className='LandingPage-available-platforms-section-content-dividing-line'></div>

                            {/* 'iPad' */}
                            <div className='LandingPage-available-platforms-section-content-col'>
                                {/* ..platform name */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-name'>iPad App</div>
                                {/* ..platform status */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-status'>Launching in Mid-2027</div>
                                {/* ..platform demo */}
                                <div className='LandingPage-available-platforms-section-content-col-website-platform-demo'>
                                    <video
                                        className='website-platform-demo-animated-logo'
                                        autoPlay
                                        loop
                                        muted
                                        playsInline>
                                        <source src={AnimatedHorizontalLogo} type="video/webm"/>
                                    </video>
                                </div>
                                {/* ..availability */}
                                <div className='LandingPage-available-platforms-section-content-col-availability-section'>Native Apple iPad iOS</div>
                                {/* ..platform cta-button */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-cta-button disabled'>Coming soon</div>
                            </div>

                            {/* --- dividing line --- */}
                            <div className='LandingPage-available-platforms-section-content-dividing-line'></div>

                            {/* 'iPhone/Android' */}
                            <div className='LandingPage-available-platforms-section-content-col'>
                                {/* ..platform name */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-name'>iPhone/Android App</div>
                                {/* ..platform status */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-status'>Launching in Mid-2027</div>
                                {/* ..platform demo */}
                                <div className='LandingPage-available-platforms-section-content-col-mobile-platform-demo'>
                                    <video
                                        className='website-platform-demo-animated-logo'
                                        autoPlay
                                        loop
                                        muted
                                        playsInline>
                                        <source src={AnimatedHorizontalLogo} type="video/webm"/>
                                    </video>
                                </div>
                                {/* ..availability */}
                                <div className='LandingPage-available-platforms-section-content-col-availability-section'>iOS, Android</div>
                                
                                {/* ..platform cta-button */}
                                <div className='LandingPage-available-platforms-section-content-col-platform-cta-button disabled'>Coming soon</div>
                            </div>
                        </div>
                    </div>
                </>
            }

            {/* Footer */}
            <FooterNew/>
            
        </div>
    );
};


// Export statement
export default LandingNew;